import * as React from 'react'
import { Gauge as GaugeIcon } from 'lucide-react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  useGetPlantDcsNodeDetailQuery,
  useGetPlantDcsTreeQuery
} from '@axilon-apps/domains-ontology-ui-state'
import { Empty } from '../../components/primitives/Empty'
import { plantDcsIndexHref } from '../../lib/plant-links'
import { DcsDetailPanel } from './DcsDetailPanel'
import { DcsTreeNav } from './DcsTreeNav'

/**
 * `/plant/dcs` — DCS tab.
 *
 * Single page for both unselected (empty pane on the right) and selected
 * (`?node=<id>`) states. The left rail and the right detail panel stay
 * mounted so tree expand/collapse survives intra-tab navigation.
 *
 * URL contract: the selected DCS node lives in `?node=` and is shared
 * across controllers, CEEs, control modules, function blocks, and I/O.
 */
const DcsPage = (): React.ReactElement => {
  const [searchParams] = useSearchParams()
  const rawParam = searchParams.get('node')
  const selectedNodeId = rawParam ? decodeURIComponent(rawParam) : null

  const tree = useGetPlantDcsTreeQuery()
  const nodes = tree.data?.nodes ?? []
  const totalControlTagCount = tree.data?.totalControlTagCount ?? 0

  const detail = useGetPlantDcsNodeDetailQuery(selectedNodeId ?? '', {
    skip: !selectedNodeId
  })

  const breadcrumbName = React.useMemo(() => {
    if (!detail.data) return selectedNodeId ?? ''
    switch (detail.data.kind) {
      case 'DcsController':
        return detail.data.controller.display_name ?? detail.data.controller.name
      case 'Cee':
        return detail.data.cee.display_name ?? detail.data.cee.name
      case 'ControlModule':
        return detail.data.module.display_name ?? detail.data.module.name
      case 'FunctionBlock':
        return detail.data.block.name
      case 'IoModule':
        return detail.data.ioModule.name
      case 'IoChannel':
        return detail.data.channel.name
      case 'ControlTag':
        return detail.data.tag.name
      case 'ControlAlarm':
        return detail.data.alarm.name
      case 'ControlSetpoint':
        return detail.data.setpoint.name
    }
  }, [detail.data, selectedNodeId])

  return (
    <div className="flex flex-1 flex-col gap-card">
      {selectedNodeId ? (
        <nav className="text-sm text-ink-3" aria-label="Breadcrumb">
          <Link to={plantDcsIndexHref()} className="hover:text-ink hover:underline">
            DCS
          </Link>
          <span className="mx-1.5 text-ink-4">/</span>
          {detail.data && (
            <>
              <span className="text-ink-3">{detail.data.kind}</span>
              <span className="mx-1.5 text-ink-4">/</span>
            </>
          )}
          <span className="text-ink">{breadcrumbName}</span>
        </nav>
      ) : (
        <header>
          <h1 className="m-0 text-2xl font-semibold text-ink">DCS</h1>
          <p className="mt-1 text-sm text-ink-3">{dcsSubtitle(nodes, totalControlTagCount)}</p>
        </header>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[17.5rem_minmax(0,1fr)]">
        <DcsTreeNav
          nodes={nodes}
          isLoading={tree.isLoading}
          selectedId={selectedNodeId ?? undefined}
        />
        <div className="flex min-w-0 flex-col gap-card">
          {selectedNodeId ? (
            <DcsDetailPanel nodeId={selectedNodeId} />
          ) : (
            <div className="flex min-h-80 flex-col rounded-xl border bg-card text-card-foreground shadow-sm">
              {tree.isError ? (
                <Empty
                  title="Couldn't load DCS tree"
                  hint="Check the proxy is reachable and try again."
                  icon={GaugeIcon}
                />
              ) : (
                <Empty
                  title="Select a DCS node"
                  hint="Pick a controller, CEE, control module, function block, or I/O module from the tree to see tags, wiring, and cross-references."
                  icon={GaugeIcon}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function dcsSubtitle(nodes: { kind: string }[], totalControlTagCount: number): string {
  if (nodes.length === 0) {
    return 'DCS controllers, CEEs, control modules, function blocks, and the I/O that they wire to.'
  }
  let controllers = 0
  let cees = 0
  let modules = 0
  let blocks = 0
  let ioModules = 0
  let channels = 0
  for (const node of nodes) {
    if (node.kind === 'DcsController') controllers += 1
    else if (node.kind === 'Cee') cees += 1
    else if (node.kind === 'ControlModule') modules += 1
    else if (node.kind === 'FunctionBlock') blocks += 1
    else if (node.kind === 'IoModule') ioModules += 1
    else if (node.kind === 'IoChannel') channels += 1
  }
  const parts: string[] = [
    `${controllers.toLocaleString()} ${controllers === 1 ? 'controller' : 'controllers'}`,
    `${cees.toLocaleString()} ${cees === 1 ? 'CEE' : 'CEEs'}`,
    `${modules.toLocaleString()} ${modules === 1 ? 'control module' : 'control modules'}`,
    `${blocks.toLocaleString()} ${blocks === 1 ? 'function block' : 'function blocks'}`,
    `${ioModules.toLocaleString()} ${ioModules === 1 ? 'I/O module' : 'I/O modules'}`,
    `${channels.toLocaleString()} ${channels === 1 ? 'channel' : 'channels'}`,
    `${totalControlTagCount.toLocaleString()} ${totalControlTagCount === 1 ? 'control tag' : 'control tags'}`
  ]
  return `${parts.join(' · ')}.`
}

export default DcsPage
